/**
 * OnboardingChecklist — collapsible setup wizard for new tenants.
 *
 * Sits at the top of the Command Center. Auto-detects step completion
 * from real account state (no flags — if you connect Gmail it just goes
 * to done, no separate "mark as done" click).
 *
 * Auto-hides once `state.completed_at` is set OR when 100% of steps are
 * either done or dismissed.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  getOnboarding, dismissOnboardingStep, completeOnboarding, resetOnboarding,
  type OnboardingStep,
} from '@/lib/onboarding';
import { useBasePath, joinNav } from '@/lib/config/nav-helpers';
import { friendlyError } from '../lib/utils/friendly-errors';

const CATEGORY_LABEL: Record<OnboardingStep['category'], string> = {
  required:    'Required',
  recommended: 'Recommended',
  optional:    'Optional',
};

const CATEGORY_COLOR: Record<OnboardingStep['category'], string> = {
  required:    'text-danger',
  recommended: 'text-warn',
  optional:    'text-ink-dim',
};

const STORAGE_KEY = 'waypoint:onboarding-collapsed';

export function OnboardingChecklist() {
  const navigate = useNavigate();
  const basePath = useBasePath();
  const queryClient = useQueryClient();

  const q = useQuery({ queryKey: ['onboarding'], queryFn: getOnboarding, staleTime: 30_000, retry: 2 });

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) === 'true'; } catch { return false; }
  });
  const setCollapsedPersist = (v: boolean) => {
    setCollapsed(v);
    try { localStorage.setItem(STORAGE_KEY, String(v)); } catch { /* noop */ }
  };

  const dismiss = useMutation({
    mutationFn: dismissOnboardingStep,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onboarding'] }),
  });
  const complete = useMutation({
    mutationFn: completeOnboarding,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onboarding'] }),
  });
  const reset = useMutation({
    mutationFn: resetOnboarding,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onboarding'] }),
  });

  // Hide entirely when whole wizard is marked complete
  if (q.data?.state.completed_at) {
    return null;
  }

  // Error state — show what failed so we can debug instead of silently rendering 0/0
  if (q.error) {
    return (
      <div className="rounded-xl border border-danger/30 bg-danger-soft/30 px-4 py-3 text-2xs text-danger">
        Couldn't load setup checklist: {friendlyError(q.error)}
        <button onClick={() => q.refetch()} className="ml-2 underline">Retry</button>
      </div>
    );
  }

  // Loading state: skeleton-ish, single line
  if (q.isLoading || !q.data) {
    return (
      <div className="rounded-xl border border-border bg-surface-1/60 px-4 py-3 text-2xs text-ink-dim">
        Loading setup checklist…
      </div>
    );
  }

  const { steps, progress } = q.data;
  const pendingSteps = steps.filter(s => s.status === 'pending');
  const completed = steps.filter(s => s.status === 'done');

  return (
    <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-linear-to-br from-primary/4 via-surface-1/40 to-surface-1/40">
      {/* Header strip */}
      <button
        onClick={() => setCollapsedPersist(!collapsed)}
        className="flex w-full items-center justify-between gap-4 px-5 py-3 text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></svg>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-base  text-ink">Get set up</span>
              <span className="rounded-full bg-surface-2 px-2 py-0.5 text-2xs font-mono font-semibold text-ink-muted tabular-nums">
                {progress.done}/{progress.total}
              </span>
            </div>
            <div className="mt-0.5 text-2xs text-ink-muted truncate">
              {pendingSteps.length === 0
                ? 'Everything required is done. Hide this once you\'re happy with it.'
                : `${pendingSteps[0]!.title} — ${pendingSteps[0]!.description.slice(0, 60)}…`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {/* Progress bar */}
          <div className="hidden sm:block w-24 h-1.5 rounded-full bg-surface-2 overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${progress.percent}%` }}
            />
          </div>
          <svg
            className={clsx('text-ink-dim transition-transform', collapsed ? '' : 'rotate-180')}
            width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </button>

      {!collapsed && (
        <div className="border-t border-border/30 px-5 py-4 space-y-3 animate-fade-in">
          {/* Group: pending (sorted by category importance) */}
          {pendingSteps
            .sort((a, b) => categoryOrder(a.category) - categoryOrder(b.category))
            .map(step => (
              <StepRow
                key={step.id}
                step={step}
                onCta={() => navigate(joinNav(basePath, step.ctaHref))}
                onSkip={() => dismiss.mutate(step.id)}
                disabled={dismiss.isPending}
              />
            ))
          }

          {/* Done summary (collapsed list of completed steps) */}
          {completed.length > 0 && (
            <details className="mt-4 group">
              <summary className="cursor-pointer text-2xs text-ink-dim hover:text-ink list-none flex items-center gap-2">
                <svg aria-hidden="true" className="transition-transform group-open:rotate-90" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
                {completed.length} step{completed.length === 1 ? '' : 's'} complete
              </summary>
              <ul className="mt-2 space-y-1 pl-4">
                {completed.map(s => (
                  <li key={s.id} className="text-2xs text-ink-dim flex items-center gap-2">
                    <svg aria-hidden="true" className="text-success shrink-0" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                    {s.title}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {/* Footer actions */}
          <div className="flex items-center justify-between pt-3 mt-3 border-t border-border/30">
            <button
              onClick={() => reset.mutate()}
              disabled={reset.isPending}
              className="text-2xs text-ink-dim hover:text-ink"
            >
              Reset checklist
            </button>
            {progress.readyToDismiss && (
              <button
                onClick={() => complete.mutate()}
                disabled={complete.isPending}
                className="rounded-md bg-primary px-3 py-1 text-2xs font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
              >
                {complete.isPending ? 'Hiding…' : 'Done — hide forever'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function categoryOrder(c: OnboardingStep['category']): number {
  return c === 'required' ? 0 : c === 'recommended' ? 1 : 2;
}

function StepRow({ step, onCta, onSkip, disabled }: { step: OnboardingStep; onCta: () => void; onSkip: () => void; disabled: boolean }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-surface-1 px-3 py-2.5 hover:border-border-strong transition-colors">
      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-border bg-bg-1 mt-0.5">
        <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-ink-dim/40"><circle cx="12" cy="12" r="6" /></svg>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2 mb-0.5">
          <span className="text-xs font-semibold text-ink">{step.title}</span>
          <span className={clsx('text-2xs uppercase tracking-wider font-semibold', CATEGORY_COLOR[step.category])}>
            {CATEGORY_LABEL[step.category]}
          </span>
        </div>
        <p className="text-2xs text-ink-muted leading-snug">{step.description}</p>
        <p className="mt-1 text-2xs text-ink-dim">
          Unlocks: <span className="text-primary">{step.unlocks}</span>
        </p>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <button
          onClick={onCta}
          className="rounded-md bg-primary px-2.5 py-1 text-2xs font-semibold text-white hover:bg-primary/90 whitespace-nowrap"
        >
          {step.cta}
        </button>
        {step.category !== 'required' && (
          <button
            onClick={onSkip}
            disabled={disabled}
            className="text-2xs text-ink-dim hover:text-ink disabled:opacity-50"
          >
            Skip
          </button>
        )}
      </div>
    </div>
  );
}
